import * as React from 'react'
import * as XLSX from 'xlsx'
import * as Papa from 'papaparse'
import { CurrentCompanyStore } from '../../../../../../stores/CurrentCompanyStore'
import { axios } from '@getgreenline/homi-shared'
import { FormError } from '../../../../../../components/FormError'
import { ComplianceStore } from '../../ComplianceStore'
import { numberPadder, parseErrorMsg } from '../../../../../../utilities/helpers'
import { Alert } from 'reactstrap'
import { Button, Checkbox } from 'antd'
import { Track } from '../../../../../../Track'

interface Props {
  currentCompanyStore: CurrentCompanyStore
  complianceStore: ComplianceStore
  nameOfStore?: string
  retailStoreAuthorization: string
}

interface State {
  isLoading: boolean
  errorMessage?: string
  consentOCSSubmission: boolean
  showSlowWarning: boolean
}

export class OntarioSalesReport extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      isLoading: false,
      consentOCSSubmission: false,
      showSlowWarning: false,
    }
  }

  informationHeaderRows = () => {
    const { nameOfStore, retailStoreAuthorization } = this.props
    const { consentOCSSubmission } = this.state
    const { month, year } = this.props.complianceStore

    const formattedMonth = numberPadder(month, 2)
    const emptyCols = new Array(4)

    const consentValue = consentOCSSubmission
      ? 'I give consent to the AGCO to provide this sales data to the Ontario Cannabis Store (OCS) on my behalf.'
      : 'I do not give consent to the AGCO to provide this sales data to the Ontario Cannabis Store (OCS) on my behalf and acknowledge that I may need to make other arrangements directly with the OCS.'

    const nameReportingStoreRow = ['Name of Reporting Retail Store', ...emptyCols, nameOfStore]
    const retailStoreAuthorizationRow = [
      'Retail Store Authorization (RSA) Number',
      ...emptyCols,
      retailStoreAuthorization,
    ]
    const reportingPeriodRow = [
      'Reporting Period: (yyyy/mm)',
      ...emptyCols,
      `${year}/${formattedMonth}`,
    ]
    const consentOCSRow = [
      'Do you give consent for the AGCO to also share this sales data with the OCS?',
      '',
      `${consentOCSSubmission ? 'YES' : 'NO'}`,
      consentValue,
    ]

    const emptyRows = new Array(4)

    return [
      ...emptyRows,
      nameReportingStoreRow,
      retailStoreAuthorizationRow,
      reportingPeriodRow,
      [],
      consentOCSRow,
    ]
  }

  generateSalesReport = async () => {
    const company = this.props.currentCompanyStore.company!
    const { month, year, locationIds } = this.props.complianceStore

    const params = {
      month,
      year,
      locationIds,
      reportType: 'sales',
    }

    Track.track('dashboard:compliance_complianceReportGenerated', params)

    try {
      this.setState({
        isLoading: true,
        errorMessage: undefined,
      })

      const showSlowWarningTimer = setTimeout(() => {
        this.setState({ showSlowWarning: true })
      }, 5000)

      const response = await axios.get(`/api/v1/company/${company.id}/reports/compliance`, {
        params: params,
      })

      clearTimeout(showSlowWarningTimer)
      this.setState({ isLoading: false, showSlowWarning: false })
      const parsedData = Papa.parse(response.data, { newline: '\r\n' })
      const informationHeaderRows = this.informationHeaderRows()

      const dataRows = [...informationHeaderRows, [], ...parsedData.data]

      const salesReporting = XLSX.utils.aoa_to_sheet(dataRows)

      const mergeNameLabel = XLSX.utils.decode_range('A5:E5')
      const mergeNameValue = XLSX.utils.decode_range('F5:I5')
      const mergeRSALabel = XLSX.utils.decode_range('A6:E6')
      const mergeRSAValue = XLSX.utils.decode_range('F6:I6')
      const mergeReportingPeriodLabel = XLSX.utils.decode_range('A7:E7')
      const mergeReportingPeriodValue = XLSX.utils.decode_range('F7:I7')
      const mergeConsentLabel = XLSX.utils.decode_range('A9:B9')
      const mergeConsentValue = XLSX.utils.decode_range('D9:I9')
      salesReporting['!merges'] = [
        mergeNameLabel,
        mergeNameValue,
        mergeRSALabel,
        mergeRSAValue,
        mergeReportingPeriodLabel,
        mergeReportingPeriodValue,
        mergeConsentLabel,
        mergeConsentValue,
      ]

      const new_workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(new_workbook, salesReporting, 'Sales Reporting')

      const fileMonth = numberPadder(month, 2)
      const fileYear = year.toString().slice(2)

      const title = `${company.name}_RSA${this.props.retailStoreAuthorization}_OCS_${fileMonth}${fileYear}.xlsx`
      XLSX.writeFile(new_workbook, title)
    } catch (error) {
      this.setState({
        isLoading: false,
        errorMessage: parseErrorMsg(error),
        showSlowWarning: false,
      })
    }
  }

  render() {
    const { isLoading, errorMessage, consentOCSSubmission } = this.state
    const { locationIds } = this.props.complianceStore

    return (
      <div>
        <h5>Sales report</h5>
        <div className='mb-2'></div>
        {this.state.showSlowWarning && (
          <Alert color='warning'>
            This is a large and complicated report and may take a long time to process. Please do
            not leave the page.
          </Alert>
        )}
        <Button
          type='primary'
          onClick={() => this.generateSalesReport()}
          loading={locationIds.length === 0 || isLoading}
        >
          {isLoading ? 'Downloading...' : 'Download sales report'}
        </Button>
        <FormError message={errorMessage} />
      </div>
    )
  }
}
